import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "i18n-fe";
import { useScoreContext } from "../context/ScoreContext";
import { unsyncedManualRides } from "../context/manualRideSyncStatus";
import {
  checkManualRideDiscard,
  discardManualRide,
  subscribeManualRides,
} from "../store/manualRides";
import { rideCnoNumber } from "../util/rideDisplay";

// #882 — a manual ride is TWO independent writes (the ride row's createRide
// outbox op, then the score sheet). If the ride op is stranded — a bad
// payload, a dead token, a network blip that outlasts the sheet's own retry —
// the SHEET can still flush while the RIDE never reaches score-api, leaving
// an orphan sheet invisible to the Review Queue (root cause of the CDS
// 2026-07-17 CNO 462 incident). This is the visible "something didn't reach
// the cloud" cue: sync.js's backoff retry + manualRides.js no longer
// swallowing queue failures fix the underlying loss, but the scribe still
// needs to SEE it's happening so a closed tab doesn't take the score with it.
//
// Counts RIDES, not sheets — a multi-judge ride is one ride with multiple
// score sheets, not multiple problems, so this must not multiply the count
// per judge.
//
// #1922 — the chip is also the second reachable place (besides the ride
// list) to discard a manual ride added by mistake: tapping it opens the list
// of unsynced manual rides behind the count, each with its own discard
// action, so a stray add doesn't have to be tracked down class-by-class.
export default function UnsyncedRidesIndicator() {
  const { unsyncedRideCount, erroredRideKeys } = useScoreContext();
  const { t } = useTranslation("score");
  const [anchorEl, setAnchorEl] = useState(null);
  const [manualRides, setManualRides] = useState([]);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardGuard, setDiscardGuard] = useState(null);
  const [discarding, setDiscarding] = useState(false);

  // Live list of manual rides for the popover — same source (localStorage
  // mirror) the count itself is derived from (ScoreContext), so a discard
  // here or from the ride list updates both without a reload.
  useEffect(() => {
    return subscribeManualRides((list) => setManualRides(list));
  }, []);

  // #1980 — filter through the SAME exclusion the chip's count uses
  // (manualRideSyncStatus.unsyncedManualRides), so a ride already covered by
  // the sync-issue chip can never still show up in this popover's list —
  // the chip and the list it opens onto must always agree on the same set
  // of rides, not just the same number.
  const unsyncedRides = useMemo(
    () => unsyncedManualRides(manualRides, erroredRideKeys),
    [manualRides, erroredRideKeys],
  );

  if (!unsyncedRideCount) return null;

  const openDiscard = (ride) => {
    setDiscardTarget(ride);
    setDiscardGuard(null);
    // #1922 judge fix — an IndexedDB read failure here used to leave
    // discardGuard permanently null, so the dialog stuck on its "…"
    // placeholder forever with no escape hatch on a kiosk. A rejection now
    // resolves to a definite, actionable refusal state instead.
    checkManualRideDiscard(ride.id)
      .then(setDiscardGuard)
      .catch(() => setDiscardGuard({ ok: false, reason: "check-failed" }));
  };
  const closeDiscard = () => {
    setDiscardTarget(null);
    setDiscardGuard(null);
    setDiscarding(false);
  };
  const confirmDiscard = async () => {
    if (!discardTarget || !discardGuard) return;
    setDiscarding(true);
    try {
      const force = discardGuard.reason === "has-score-data";
      const result = await discardManualRide(discardTarget.id, { force });
      if (result.ok) {
        closeDiscard();
      } else {
        setDiscardGuard(result);
      }
    } finally {
      // #1922 judge fix — a rejected discardManualRide (e.g. IndexedDB
      // removal fails) used to skip this and leave the confirm button
      // disabled forever with no escape hatch on a kiosk.
      setDiscarding(false);
    }
  };

  return (
    <>
      <Chip
        data-testid="unsynced-rides-indicator"
        size="small"
        icon={<ReportProblemIcon />}
        label={t("status.unsyncedRides", { count: unsyncedRideCount })}
        color="error"
        variant="filled"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          fontWeight: 600,
          cursor: "pointer",
          // Brand `primary` reads as dark grey and disappears on grey.900 in
          // dark mode (see other chips in this file's siblings) — MUI's
          // semantic `error` color carries its own contrastText, so no
          // override is needed here, but pin it explicitly so a future theme
          // change can't silently make this illegible again.
          color: "error.contrastText",
          "& .MuiChip-icon": { color: "error.contrastText" },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ minWidth: 280, maxWidth: 360, p: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, color: "text.secondary" }}>
            {t("status.unsyncedRides", { count: unsyncedRideCount })}
          </Typography>
          <List dense data-testid="unsynced-rides-list">
            {unsyncedRides.map((ride) => (
              <ListItem
                key={ride.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={t("rides.discard.ariaLabel", { number: rideCnoNumber(ride) })}
                    data-testid="discard-manual-ride-from-chip"
                    onClick={() => openDiscard(ride)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={t("rides.ridePrefix", { number: rideCnoNumber(ride) })}
                  secondary={`${ride.horseName} / ${ride.riderName}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      {/* Same guarded discard flow as RideList.jsx's, reachable here too
          (AC1) — see that component for why the states are split this way. */}
      <Dialog
        open={Boolean(discardTarget)}
        onClose={closeDiscard}
        maxWidth="xs"
        fullWidth
        data-testid="discard-manual-ride-dialog"
      >
        <DialogTitle>{t("rides.discard.title")}</DialogTitle>
        <DialogContent>
          {discardTarget && discardGuard === null && (
            <DialogContentText>&hellip;</DialogContentText>
          )}
          {discardTarget && discardGuard?.ok && (
            <DialogContentText data-testid="discard-body">
              {t("rides.discard.body", {
                number: rideCnoNumber(discardTarget),
                horse: discardTarget.horseName,
                rider: discardTarget.riderName,
              })}
            </DialogContentText>
          )}
          {discardTarget && discardGuard?.reason === "has-server-id" && (
            <Alert severity="warning" data-testid="discard-refused-server-id">
              {t("rides.discard.refusedServerId")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "not-found" && (
            <Alert severity="warning" data-testid="discard-refused-not-found">
              {t("rides.discard.refusedNotFound")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "create-in-flight" && (
            <Alert severity="info" data-testid="discard-refused-in-flight">
              {t("rides.discard.refusedInFlight")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "check-failed" && (
            <Alert severity="warning" data-testid="discard-check-failed">
              {t("rides.discard.checkFailed")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "has-score-data" && (
            <Alert severity="error" data-testid="discard-score-data-warning">
              {t("rides.discard.scoreDataWarning", {
                number: rideCnoNumber(discardTarget),
                horse: discardTarget.horseName,
                rider: discardTarget.riderName,
                detail: [
                  discardGuard.scoreSheetCount
                    ? t("rides.discard.scoreDataScoreSheets", {
                        count: discardGuard.scoreSheetCount,
                      })
                    : null,
                  discardGuard.queuedOpCount
                    ? t("rides.discard.scoreDataQueuedOps", {
                        count: discardGuard.queuedOpCount,
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(", "),
              })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDiscard}>
            {t("rides.discard.cancel")}
          </Button>
          {(discardGuard?.ok || discardGuard?.reason === "has-score-data") && (
            <Button
              color="error"
              variant="contained"
              onClick={confirmDiscard}
              disabled={discarding}
              data-testid="discard-confirm-button"
            >
              {discardGuard.reason === "has-score-data"
                ? t("rides.discard.confirmAnyway")
                : t("rides.discard.confirm")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
